import { CommentResponse } from '@/api/data-contracts';
import { ModuleType } from '@/shared/lib/hooks/useModuleChipColors';

export type CommentWithUnread = CommentResponse & { isUnread: boolean };

export const computeHasUnread = (
  threads:
    | Record<string, CommentWithUnread[]>
    | Record<string, CommentResponse[]>
    | any[]
    | undefined,
): boolean => {
  if (!threads) return false;

  if (Array.isArray(threads)) {
    return threads.some((c) => (c as any).isUnread === true);
  }

  const allComments = Object.values(threads).flat();
  return allComments.some((c) => (c as any).isUnread === true);
};

export type PlatformChipInfo = { userType: ModuleType; label: string };

export const getPlatformChipInfo = (platform: string | undefined): PlatformChipInfo | null => {
  switch (platform?.toUpperCase()) {
    case 'LOCATION':
      return { userType: 'location', label: 'Location' };
    case 'ADVERTISER':
      return { userType: 'advertiser', label: 'Advertiser' };
    case 'SERVICE_PROVIDER':
    case 'SERVICES':
    case 'SERVICE':
      return { userType: 'service', label: 'Services' };
    case 'BACKOFFICE':
      return { userType: 'backoffice', label: 'Backoffice' };
    default:
      return null;
  }
};

export const formatRole = (position: string | undefined): string | null => {
  if (!position) return null;
  const formatted = position
    .trim()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  return formatted || null;
};

const normalizeUserType = (type: string): string => {
  const lower = type.toLowerCase();
  if (lower === 'services' || lower === 'service_provider') return 'service';
  return lower;
};

export const injectIsUnread = <T extends Record<string, CommentResponse[]>>(
  comments: T,
  watermark: string | null | undefined,
  currentUserId: string,
  currentUserType?: string,
  localWatermark?: string | null,
): Record<string, CommentWithUnread[]> => {
  const result: Record<string, CommentWithUnread[]> = {};
  const watermarkDate = watermark ? new Date(watermark).getTime() : null;
  const localWatermarkDate =
    localWatermark !== undefined
      ? localWatermark
        ? new Date(localWatermark).getTime()
        : null
      : watermarkDate;

  for (const [key, list] of Object.entries(comments)) {
    result[key] = list.map((c) => {
      const isCrossPortal =
        currentUserType !== undefined &&
        (c.author.userType == null ||
          normalizeUserType(c.author.userType) !== normalizeUserType(currentUserType));

      const effectiveWatermarkDate = isCrossPortal ? localWatermarkDate : watermarkDate;
      return {
        ...c,
        isUnread:
          (c.author.id !== currentUserId || isCrossPortal) &&
          (!effectiveWatermarkDate || new Date(c.created).getTime() > effectiveWatermarkDate),
      };
    });
  }
  return result;
};

/** True when any attachment in the unread map is unread and not locally marked read. */
export const computeHasAnyUnread = (
  unreadMap: Record<string, boolean> | undefined,
  locallyReadIds: Set<string>,
): boolean => {
  if (!unreadMap) return false;
  return Object.entries(unreadMap).some(([id, unread]) => unread && !locallyReadIds.has(id));
};

/** Copy of the unread map with every locally-read attachment forced to read (false). */
export const applyLocalReads = (
  unreadMap: Record<string, boolean> | undefined,
  locallyReadIds: Set<string>,
): Record<string, boolean> => {
  if (!unreadMap) return {};
  const result = { ...unreadMap };
  locallyReadIds.forEach((id) => {
    result[id] = false;
  });
  return result;
};
